import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Check, ArrowLeft, ShoppingBag } from 'lucide-react';
import { products } from '../data/mock';
import BuyModal from '../components/BuyModal';

const ProductDetail = () => {
  const { id } = useParams();
  const product = products.find(p => p.id === parseInt(id));
  const [selectedImage, setSelectedImage] = useState(0);
  const [showBuyModal, setShowBuyModal] = useState(false);

  if (!product) {
    return (
      <div className="section-padding container">
        <p className="body-large">Product not found</p>
        <Link to="/products" className="btn-secondary">
          Back to Products
        </Link>
      </div>
    );
  }

  return (
    <div className="section-padding" style={{ backgroundColor: '#fffef2' }}>
      <div className="container">
        {/* Back Button */}
        <Link 
          to="/products" 
          className="btn-icon"
          style={{ marginBottom: '32px' }}
        >
          <ArrowLeft size={16} />
          <span>Back to Products</span>
        </Link>

        <div className="grid-two-column">
          {/* Product Images */}
          <div>
            <div style={{
              backgroundColor: '#fff',
              padding: '40px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img 
                src={product.gallery[selectedImage]}
                alt={product.name}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  maxHeight: '500px'
                }}
              />
            </div>
            
            {/* Image Gallery */}
            <div style={{
              display: 'flex',
              gap: '16px',
              overflowX: 'auto'
            }}>
              {product.gallery.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  style={{
                    backgroundColor: '#fff',
                    padding: '16px',
                    border: selectedImage === index ? '2px solid #333333' : '1px solid #bcbbb4',
                    borderRadius: '0px',
                    cursor: 'pointer',
                    minWidth: '100px',
                    height: '100px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img 
                    src={img}
                    alt={`${product.name} ${index + 1}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div>
            <div style={{
              display: 'inline-block',
              padding: '4px 12px',
              backgroundColor: '#ebeade',
              marginBottom: '16px',
              borderRadius: '0px'
            }}>
              <span className="body-small">{product.category}</span>
            </div>

            <h1 className="hero-medium" style={{ marginBottom: '16px' }}>
              {product.name}
            </h1>

            {/* Rating */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Star size={18} fill="#333333" color="#333333" />
                <span className="body-regular" style={{ fontWeight: '500' }}>
                  {product.rating}
                </span>
              </div>
              <span className="body-regular" style={{ color: '#666666' }}>
                ({product.reviews} reviews)
              </span>
            </div>

            {/* Price */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'baseline', 
              gap: '12px',
              marginBottom: '32px'
            }}>
              <span className="hero-medium">₹{product.price.toLocaleString()}</span>
              <span className="body-regular" style={{ 
                color: '#bcbbb4', 
                textDecoration: 'line-through' 
              }}>
                ₹{product.originalPrice.toLocaleString()}
              </span>
              <span className="body-regular" style={{ 
                color: '#ba3e2b',
                fontWeight: '500'
              }}>
                {product.discount}% off
              </span>
            </div>

            {/* Description */}
            <p className="body-regular" style={{ 
              color: '#666666',
              marginBottom: '32px',
              lineHeight: '1.7'
            }}>
              {product.description}
            </p>

            {/* Stock Status */}
            <div style={{ marginBottom: '32px' }}>
              <span className="body-regular" style={{
                color: product.inStock ? '#333333' : '#ba3e2b',
                fontWeight: '500'
              }}>
                {product.inStock ? '✓ In Stock' : '✗ Out of Stock'}
              </span>
            </div>

            {/* Buy Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              marginBottom: '48px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => setShowBuyModal(true)}
                className="btn-primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <ShoppingBag size={16} />
                Buy Now
              </button>
              <a 
                href={product.amazonLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '19px 23px',
                  border: '1px solid #bcbbb4'
                }}
              >
                Buy on Amazon
              </a>
            </div>

            {/* Features */}
            <div style={{ 
              borderTop: '1px solid #ebeade',
              paddingTop: '32px',
              marginBottom: '32px'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                Key Features
              </h3>
              <ul style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {product.features.map((feature, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <Check size={20} color="#333333" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span className="body-regular" style={{ color: '#666666' }}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Specifications */}
            <div style={{ 
              borderTop: '1px solid #ebeade',
              paddingTop: '32px'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                Specifications
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {Object.entries(product.specifications).map(([key, value]) => (
                  <div key={key} style={{
                    display: 'grid',
                    gridTemplateColumns: '150px 1fr',
                    gap: '16px'
                  }}>
                    <span className="body-regular" style={{ color: '#666666' }}>
                      {key}
                    </span>
                    <span className="body-regular" style={{ fontWeight: '500' }}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        <div style={{ marginTop: '80px' }}>
          <h2 className="heading-2" style={{ marginBottom: '32px' }}>
            You May Also Like
          </h2>
          <div className="grid-product-showcase">
            {products
              .filter(p => p.id !== product.id)
              .slice(0, 3)
              .map(relatedProduct => (
                <Link 
                  key={relatedProduct.id}
                  to={`/product/${relatedProduct.id}`}
                  className="product-card hover-lift"
                >
                  <img 
                    src={relatedProduct.image}
                    alt={relatedProduct.name}
                    className="product-card-image"
                    style={{ backgroundColor: '#fff' }}
                  />
                  <div style={{ padding: '16px' }}>
                    <h3 className="product-card-title">
                      {relatedProduct.name}
                    </h3>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'baseline', 
                      gap: '8px' 
                    }}>
                      <span className="heading-3">₹{relatedProduct.price.toLocaleString()}</span>
                      <span className="body-small" style={{ color: '#ba3e2b' }}>
                        {relatedProduct.discount}% off
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </div>

      {/* Buy Modal */}
      {showBuyModal && (
        <BuyModal 
          product={product} 
          onClose={() => setShowBuyModal(false)} 
        />
      )}
    </div>
  );
};

export default ProductDetail;
